import { formatearFechaHora, formatearRevision, NOMBRE_ETAPA } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { BotonEnlace, CABECERA, EstadoVacio, Pildora, Tabla, TD, TH } from "../componentes/ui.tsx";
import { abreviarNombre } from "../utiles.ts";

/** Bandeja "Pendientes de firma": las revisiones cuya etapa actual me tiene asignado. */
export function PaginaPendientes() {
  const trpc = useTRPC();
  const pendientes = useQuery(trpc.documentos.pendientes.queryOptions());

  return (
    <div>
      <Encabezado overline="Sistema" titulo="Pendientes de firma" />
      {pendientes.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {pendientes.data && pendientes.data.length === 0 && (
        <EstadoVacio>No tienes firmas pendientes.</EstadoVacio>
      )}
      {pendientes.data && pendientes.data.length > 0 && (
        <Tabla minimo={720}>
          <thead>
            <tr className={CABECERA}>
              <th className={TH}>Documento</th>
              <th className={TH}>Proceso</th>
              <th className={TH}>Rev.</th>
              <th className={TH}>Te toca</th>
              <th className={TH}>Desde</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {pendientes.data.map((p) => (
              <tr key={p.revisionId} className="border-b border-borde/60 last:border-0">
                <td className={TD}>
                  <div className="font-mono text-[12px] text-marino">{p.codigo}</div>
                  <div className="font-semibold">{p.nombre}</div>
                  <div className="line-clamp-2 text-[11.5px] text-texto-3" title={p.motivo}>
                    {p.motivo} · {abreviarNombre(p.elaboradoPor)}
                  </div>
                  {p.devuelta && p.ultimaObservacion && (
                    <div className="mt-1 border-l-2 border-rojo pl-2 text-[11.5px] text-rojo">
                      {p.ultimaObservacion}
                    </div>
                  )}
                </td>
                <td className={`${TD} text-texto-2`}>{p.proceso.nombre}</td>
                <td className={`${TD} font-mono text-[12.5px]`}>{formatearRevision(p.numero)}</td>
                <td className={TD}>
                  {p.devuelta ? (
                    <Pildora tono="bg-rojo-fondo text-rojo">Devuelta</Pildora>
                  ) : (
                    <Pildora tono="bg-ambar-fondo text-ambar">
                      {p.estado === "borrador"
                        ? p.sinRevisor
                          ? "Enviar a aprobación"
                          : "Enviar a revisión"
                        : NOMBRE_ETAPA[p.etapa as keyof typeof NOMBRE_ETAPA]}
                    </Pildora>
                  )}
                </td>
                <td className={`${TD} text-texto-2`}>
                  {formatearFechaHora(p.enviadaEn ?? p.creadoEn)}
                </td>
                <td className={`${TD} text-right`}>
                  {p.devuelta ? (
                    // Devuelta: primero se corrige (archivo nuevo, observaciones); firmar después.
                    <BotonEnlace to="/documentos/$codigo" params={{ codigo: p.codigo }} compacto>
                      Corregir
                    </BotonEnlace>
                  ) : p.estado !== "borrador" ? (
                    // Fase 4: quien revisa o aprueba puede leer y marcar (pantalla 09), o firmar
                    // directo si no hay nada que corregir.
                    <span className="inline-flex gap-2">
                      <BotonEnlace
                        to="/documentos/$codigo/revisar"
                        params={{ codigo: p.codigo }}
                        compacto
                      >
                        Revisar
                      </BotonEnlace>
                      <BotonEnlace
                        to="/documentos/$codigo"
                        params={{ codigo: p.codigo }}
                        search={{ firmar: true }}
                        variante="firma"
                        compacto
                      >
                        Firmar
                      </BotonEnlace>
                    </span>
                  ) : (
                    <BotonEnlace
                      to="/documentos/$codigo"
                      params={{ codigo: p.codigo }}
                      search={{ firmar: true }}
                      variante="firma"
                      compacto
                    >
                      Firmar
                    </BotonEnlace>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </div>
  );
}
